import { useEffect, useState } from 'react';
import { Button, Pagination, Radio, Popconfirm, Badge, Popover, notification } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { createInitialRecord, dataForTableEmployeesSchedule, getErrorData } from 'shared/helpers/utils';
import { VacationsScheduleModel } from 'entities/vacationsSchedule';
import { viewerModel } from 'entities/viewer';
import { uiModel } from 'entities/ui';
import {
  useAllowCheckSchedulingQuery,
  useAllowSchedulingMutation,
  useApproveSeveralEmployeesVSMutation,
  useLazyGetEmployeesScheduleQuery,
  useLazySendToZupCheckQuery,
  useSendToZupCheckQuery,
} from 'app/services/edpApiV1/vacationsSchedule';
import { useAppDispatch, useAppSelector } from 'app/store';
import { TVSSearchParams } from 'shared/types/types';
import { useNotifications } from 'shared/hooks/use-notifications';
import { useIndicators } from 'features/indicators';
import { ONE_MINUTE } from 'shared/constants';
import { useScrollUpPage } from 'shared/hooks';
import { Link } from 'react-router-dom';
import { TScheduleData } from 'shared/types/vacation-schedule.types';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { getMultiSelectedVacations, resetVacations, setVacations } from 'entities/vacations/model/multiSelect';
import ChekboxPick from 'features/vacationsScheduleSubordinates/checboxPick/ChekboxPick';
import { RequestStatusType, RolesUser } from 'shared/enums';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import Panel from './panel';
import Cards from './cards';
import style from './index.module.scss';

export const blue = '#1677ff';

const VacationsSchedule = () => {
  const {
    role,
    organization: { id: orgId },
  } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const [currentYear, setCurrentYear] = useState<number>(
    new Date(new Date().setFullYear(new Date().getFullYear() + 1)).getFullYear(),
  );
  const [oldYearStatus, setOldYearStatus] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [currentTotal, setCurrentTotal] = useState<number>(1);
  const [btnToSendZupStatus, setBtnToSendZupStatus] = useState<boolean>(false);
  const [currentSearchValue, setCurrentSearchValue] = useState<string | undefined>(undefined);
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const multiVacations = useAppSelector(getMultiSelectedVacations);

  const dispatch = useAppDispatch();
  const [approveSeveral] = useApproveSeveralEmployeesVSMutation();
  const setEmployees = (data: TScheduleData[]) => dispatch(uiModel.VacationSchedule.actions.setEmployees(data));

  const paginationSelector = useAppSelector(
    VacationsScheduleModel.getEmployeesSchedule.selectors.employeesSchedulePagination,
  );

  const employees = useAppSelector(uiModel.VacationSchedule.selectors.getEmployees);

  const { isUploadTo1S, calendarTotalDays, firstRange, initialEmployeeRecord } = useAppSelector(
    uiModel.VacationSchedule.selectors.selectVacationSchedule,
  );

  const filterState = useAppSelector(uiModel.VacationSchedule.selectors.getFiltersState);

  const options = [
    { label: new Date().getFullYear(), value: new Date().getFullYear() },
    {
      label: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).getFullYear(),
      value: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).getFullYear(),
    },
  ];
  // Добавляем пуллинг обновления списка и обновляем индикаторы через хук useIndicators
  const [getEmployeesTrigger, resultEmployees] = useLazyGetEmployeesScheduleQuery({ pollingInterval: ONE_MINUTE });

  const resultToZupCheck = useSendToZupCheckQuery(null, {
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });

  const [triggerSendToZupPolling] = useLazySendToZupCheckQuery();

  const { status: allowCheckStatus } = useAllowCheckSchedulingQuery(null, {
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });

  const [allowSchedullingTrigger, resultAllow] = useAllowSchedulingMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });

  const {
    isSuccess: successllowScheduling,
    isError: errorllowScheduling,
    reset: resetllowScheduling,
    error: erroMessage,
  } = resultAllow;

  const disabledToallowSchedulling = oldYearStatus || allowCheckStatus !== 'fulfilled' || isUploadTo1S;

  const onChangeYear = ({ target: { value } }: RadioChangeEvent) => {
    setCurrentYear(value);
    getEmployeesTrigger({
      year: value,
      filterEmployees: filterState.filterEmployees,
      size: currentSize,
      page: currentPage,
      allAvailableOrganizations,
    });
  };

  const onChangeOptions = (page: number, pageSize: number) => {
    const querryParams: TVSSearchParams = {
      year: currentYear,
      size: pageSize,
      page,
      filterEmployees: filterState.filterEmployees,
      filterRequestStatuses: filterState.filterRequestStatuses ? ['consideredByHR'] : undefined,
      divisionId: filterState.divisionId ? filterState.divisionId : undefined,
      search: currentSearchValue ? currentSearchValue : undefined,
      allAvailableOrganizations,
    };
    getEmployeesTrigger(querryParams);
    setCurrentPage(page);
    setCurrentSize(pageSize);
  };

  useEffect(() => {
    const requestStatus = resultToZupCheck?.error?.zupRequest?.status;
    if (requestStatus) {
      if (requestStatus === 'success') {
        if (!isUploadTo1S) dispatch(uiModel.VacationSchedule.actions.isUploadTo1S(true));
        setBtnToSendZupStatus(true);
      } else {
        setBtnToSendZupStatus(false);
      }

      if (requestStatus === 'processing') {
        if (!isUploadTo1S) dispatch(uiModel.VacationSchedule.actions.isUploadTo1S(true));
        triggerSendToZupPolling(null);
      }

      if (requestStatus !== 'processing' && requestStatus !== 'success') {
        dispatch(uiModel.VacationSchedule.actions.isUploadTo1S(false));
        triggerSendToZupPolling(null);
      }
    }
  }, [resultToZupCheck?.error?.zupRequest?.status]);

  useEffect(() => {
    if (currentYear === new Date(new Date().setFullYear(new Date().getFullYear() + 1)).getFullYear()) {
      setOldYearStatus(false);
    } else {
      setOldYearStatus(true);
    }
  }, [currentYear]);

  useEffect(() => {
    if (paginationSelector) {
      setCurrentTotal(paginationSelector?.total);
    }
  }, [paginationSelector, setCurrentPage, setCurrentTotal]);

  useNotifications(
    successllowScheduling,
    errorllowScheduling,
    'Планирование разрешено',
    erroMessage?.message,
    resetllowScheduling,
  );

  useScrollUpPage(currentPage);

  useEffect(() => {
    const employeesArr = resultEmployees?.data?.data;
    const dataTable = dataForTableEmployeesSchedule(resultEmployees?.data?.data);
    // записываю в стор  последовательность id списка сотрудников в виде строки как идентификтор начального состояния
    // это нужно для блокировки ненужного запроса на получение списка сотрудников при очистке значения для поиска,
    // но не делая запроса на поиск по этому значению. DP-940
    if (employeesArr) {
      if (initialEmployeeRecord === '') {
        dispatch(uiModel.VacationSchedule.actions.setInitialEmployeesRecord(createInitialRecord(employeesArr)));
      } else {
        // сравниваю похожи ли данные на данные при инициализации если да помечаю статус как инициализованный
        if (createInitialRecord(employeesArr) === initialEmployeeRecord) {
          dispatch(uiModel.VacationSchedule.actions.setInitialEmployeesStatus(true));
        }
      }
    }

    if (dataTable) {
      setEmployees(dataTable);
    }
  }, [resultEmployees?.data?.data]);

  useEffect(() => {
    if (filterState.active) return;
    setCurrentPage(1);
    if (orgId || allAvailableOrganizations) {
      getEmployeesTrigger({
        year: currentYear,
        filterEmployees: 'all',
        size: currentSize,
        page: currentPage,
        allAvailableOrganizations,
      });
    }
  }, [orgId, allAvailableOrganizations]);

  useEffect(
    () => () => {
      dispatch(dispatch(uiModel.VacationSchedule.actions.reset()));
    },
    [],
  );
  // подписываемся на обновление индикаторов, после обновления списка и отключаем пуллинг звпроса индикаторов
  useIndicators(resultEmployees.isSuccess, resultEmployees.isFetching, false);

  const clearAllData = () => {
    dispatch(resetVacations());
  };

  const approve = async () => {
    try {
      await approveSeveral({ employeeIds: multiVacations.data });
      notification.success({ message: 'Согласовано' });
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
  };

  const pickAllData = () => {
    const filteredData = employees?.filter(elem => {
      const isOnHRForScheduledRequest =
        (elem?.scheduleRequests?.length || 0) > 0 &&
        elem.scheduleRequests?.every(elem => elem?.status?.type === RequestStatusType.consideredByHR);

      if (isOnHRForScheduledRequest) {
        return true;
      }
      return false;
    });

    const preparedData = filteredData?.map(elem => elem.id);
    if (!preparedData) {
      return;
    }
    const unionPickedData = [...preparedData, ...multiVacations.data];
    const uniquePickedData = Array.from(new Set(unionPickedData));
    dispatch(setVacations(uniquePickedData));
  };

  return (
    <>
      <div className={style.wrapShedule}>
        <div className={style.wrapSelectBtns}>
          <h3 className={style.wrapSheduleTitle}>График отпусков</h3>
          <Radio.Group
            options={options}
            onChange={onChangeYear}
            value={currentYear}
            optionType="button"
            buttonStyle="solid"
          />
        </div>
        {(role === RolesUser.HRDirector || role === RolesUser.SecondLine) && (
          <Popconfirm
            placement="leftTop"
            title="Вы уверены, что готовы начать планирование отпусков для сотрудников?"
            okText="Да"
            cancelText="Нет"
            onConfirm={() => allowSchedullingTrigger(null)}
            disabled={disabledToallowSchedulling}
          >
            <div className={style.buttons}>
              {role === RolesUser.HRDirector && (
                <Popover
                  content={
                    <ChekboxPick
                      clearAllData={clearAllData}
                      approve={approve}
                      multivacations={multiVacations.data}
                      pickAllData={pickAllData}
                    />
                  }
                  title="Выберите действие"
                  trigger="click"
                >
                  <Button>
                    Согласовать группу
                    {multiVacations.data.length !== 0 && (
                      <Badge count={multiVacations.data.length} color={blue} showZero />
                    )}
                  </Button>
                </Popover>
              )}
              {!allAvailableOrganizations && (role === RolesUser.HRDirector || role === RolesUser.SecondLine) && (
                <Link to={'/vacation-schedule/accept-settings'}>
                  <Button>Настройка согласования</Button>
                </Link>
              )}
              {!allAvailableOrganizations && role === RolesUser.HRDirector && (
                <Button type="primary" disabled={disabledToallowSchedulling}>
                  Начать планирование
                </Button>
              )}
            </div>
          </Popconfirm>
        )}
      </div>

      <Panel
        filterState={filterState}
        employeesTriger={getEmployeesTrigger}
        currentYear={currentYear}
        currentSize={currentSize}
        btnToSendZupStatus={btnToSendZupStatus}
        currentSearchValue={currentSearchValue}
        setCurrentSearchValue={setCurrentSearchValue}
        isUploadTo1S={isUploadTo1S}
        setCurrentPage={setCurrentPage}
        role={role}
      />

      {employees && (
        <div className={style.wrapContent}>
          <Cards
            filterState={filterState}
            employees={employees}
            oldYearStatus={oldYearStatus}
            currentSearchValue={currentSearchValue}
            setCurrentSearchValue={setCurrentSearchValue}
            currentYear={currentYear}
            currentPage={currentPage}
            currentSize={currentSize}
            isUploadTo1S={isUploadTo1S}
            calendarTotalDays={calendarTotalDays}
            firstRange={firstRange}
            employeesTriger={getEmployeesTrigger}
          />
          {paginationSelector && (
            <div className={style.paginationWrap}>
              <Pagination
                showSizeChanger
                defaultCurrent={currentSize}
                current={currentPage}
                pageSize={currentSize}
                total={currentTotal}
                onChange={(value: number, pageSize: number) => onChangeOptions(value, pageSize)}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default VacationsSchedule;
